import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import './AgencySupportIssues.css';
import {
  getSupportIssues,
  createSupportIssue,
  updateSupportIssue,
} from '../../services/supportIssuesService';
import { getCurrentUser } from '../../services/authService';
import { getAgencies } from '../../services/agenciesService';

// Map API response to component format (same as admin view)
const mapSupportIssueFromAPI = (apiIssue = {}) => {
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch (e) {
      return dateString;
    }
  };

  const normalizeStatus = (status) => {
    if (!status) return 'Open';
    const s = String(status).toLowerCase().replace(/_/g, ' ');
    if (s.includes('progress')) return 'In Progress';
    if (s.includes('pending')) return 'Pending';
    if (s.includes('resolve')) return 'Resolved';
    if (s.includes('close')) return 'Resolved';
    return s
      .split(' ')
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
      .join(' ');
  };

  const normalizePriority = (priority) => {
    if (!priority) return 'Medium';
    const p = String(priority).toLowerCase();
    if (p === 'high') return 'High';
    if (p === 'medium') return 'Medium';
    if (p === 'low') return 'Low';
    return p.charAt(0).toUpperCase() + p.slice(1);
  };

  return {
    id: apiIssue.log_id || apiIssue.issue_code || apiIssue.id?.toString() || '',
    issueRaiseDate:
      formatDate(apiIssue.issue_raise_date) || formatDate(apiIssue.created_at),
    completedDate:
      formatDate(apiIssue.completed_date) || formatDate(apiIssue.resolved_at),
    agency: apiIssue.agency_name || apiIssue.agency || '',
    userRole: apiIssue.user_role || apiIssue.reported_by_role || 'User',
    userName: apiIssue.user_name || apiIssue.reported_by_name || '',
    customerName: apiIssue.customer_name || '',
    mobileNumber: apiIssue.mobile_number || apiIssue.contact_phone || '',
    email: apiIssue.email || apiIssue.contact_email || '',
    issueTitle: apiIssue.issue_title || apiIssue.title || '',
    issueDescription: apiIssue.issue_description || apiIssue.description || '',
    priority: normalizePriority(apiIssue.priority),
    status: normalizeStatus(apiIssue.status),
    _raw: apiIssue,
  };
};

const AgencySupportIssues = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [issueRaiseDateFilter, setIssueRaiseDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [issueRaiseDateDropdownOpen, setIssueRaiseDateDropdownOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [priorityDropdownOpen, setPriorityDropdownOpen] = useState(false);
  const [showEntriesDropdownOpen, setShowEntriesDropdownOpen] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingIssue, setEditingIssue] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingIssue, setViewingIssue] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  // Get initial user info
  const getInitialUserInfo = () => {
    const user = getCurrentUser();
    if (user) {
      return {
        userName: user.name || user.user_name || user.full_name || user.username || '',
        mobileNumber: user.mobile_number || user.phone || user.contact_number || user.mobile || '',
        email: user.email || user.email_id || '',
      };
    }
    return {
      userName: '',
      mobileNumber: '',
      email: '',
    };
  };

  // Get current agency info from localStorage
  const getCurrentAgencyInfo = () => {
    try {
      const savedAgency = localStorage.getItem('agencyProfile');
      if (savedAgency) {
        const agency = JSON.parse(savedAgency);
        return {
          id: agency.id || agency.agency_id,
          name: agency.agency_name || agency.name || agency.company_name || '',
        };
      }
      // Fallback to userData if agencyProfile not found
      const userData = localStorage.getItem('userData');
      if (userData) {
        const user = JSON.parse(userData);
        if (user.agency_id || user.agency) {
          return {
            id: user.agency_id || user.agency?.id,
            name: user.agency?.agency_name || user.agency?.name || user.agency_name || '',
          };
        }
      }
    } catch (error) {
      console.error('Error getting agency info:', error);
    }
    return { id: null, name: '' };
  };

  const [formData, setFormData] = useState(() => {
    const userInfo = getInitialUserInfo();
    const agencyInfo = getCurrentAgencyInfo();
    return {
      issueTitle: '',
      issueDescription: '',
      issueType: 'Technical Issue', // Default issue type
      userName: userInfo.userName,
      mobileNumber: userInfo.mobileNumber,
      email: userInfo.email,
      agency: agencyInfo.name, // Auto-populate with current agency name
      agencyId: agencyInfo.id, // Store agency ID for API submission
      priority: 'Low',
      status: 'open',
      attachments: []
    };
  });
  const [formErrors, setFormErrors] = useState({});
  const [isDragging, setIsDragging] = useState(false);

  const issueRaiseDateDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);
  const priorityDropdownRef = useRef(null);
  const showEntriesDropdownRef = useRef(null);

  const [issueLogs, setIssueLogs] = useState([]);

  const statusOptions = ['Open', 'In Progress', 'Resolved'];
  const priorityOptions = ['High', 'Medium', 'Low'];
  const issueTypeOptions = ['Technical Issue', 'Billing Issue', 'Feature Request', 'Bug Report', 'Account Issue', 'Other'];

  // Filter logs
  const filteredLogs = useMemo(() => {
    return issueLogs.filter((log) => {
      const matchesIssueRaiseDate =
        !issueRaiseDateFilter || log.issueRaiseDate === issueRaiseDateFilter;
      const matchesStatus = !statusFilter || log.status === statusFilter;
      const matchesPriority = !priorityFilter || log.priority === priorityFilter;
      const matchesSearch =
        !searchQuery ||
        log.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        log.userName.toLowerCase().includes(searchQuery.toLowerCase());

      return (
        matchesIssueRaiseDate && matchesStatus && matchesPriority && matchesSearch
      );
    });
  }, [issueLogs, issueRaiseDateFilter, statusFilter, priorityFilter, searchQuery]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentLogs = filteredLogs;

  // Get current user info
  const getCurrentUserInfo = useCallback(() => {
    const user = getCurrentUser();
    if (user) {
      return {
        userName: user.name || user.user_name || user.full_name || user.username || '',
        mobileNumber: user.mobile_number || user.phone || user.contact_number || user.mobile || '',
        email: user.email || user.email_id || '',
      };
    }
    return {
      userName: '',
      mobileNumber: '',
      email: '',
    };
  }, []);

  // No need to fetch agencies for agency users - they can only create issues for their own agency

  // Fetch support issues from API
  const fetchSupportIssues = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const offset = (currentPage - 1) * itemsPerPage;

      const params = {
        limit: itemsPerPage,
        offset,
      };

      if (searchQuery) params.search = searchQuery;
      // For filters, send values exactly as API expects (title case enums)
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;

      const response = await getSupportIssues(params);

      let issuesData = [];
      let paginationData = {};

      if (response?.data?.items && Array.isArray(response.data.items)) {
        issuesData = response.data.items;
        paginationData = {
          total: response.data.total || 0,
          limit: response.data.limit || itemsPerPage,
          offset: response.data.offset || offset,
        };
      } else if (Array.isArray(response?.data)) {
        issuesData = response.data;
      } else if (Array.isArray(response)) {
        issuesData = response;
      }

      const mappedIssues = issuesData.map(mapSupportIssueFromAPI);
      setIssueLogs(
        mappedIssues.map((log) => ({
          ...log,
          attachments: log.attachments || [],
        }))
      );
      setTotalCount(paginationData.total || mappedIssues.length);
    } catch (err) {
      console.error('Error fetching support issues:', err);
      setError(err.message || 'Failed to fetch support issues. Please try again.');
      setIssueLogs([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchQuery, statusFilter, priorityFilter]);

  useEffect(() => {
    fetchSupportIssues();
  }, [fetchSupportIssues]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (issueRaiseDateDropdownRef.current && !issueRaiseDateDropdownRef.current.contains(event.target)) {
        setIssueRaiseDateDropdownOpen(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setStatusDropdownOpen(false);
      }
      if (priorityDropdownRef.current && !priorityDropdownRef.current.contains(event.target)) {
        setPriorityDropdownOpen(false);
      }
      if (showEntriesDropdownRef.current && !showEntriesDropdownRef.current.contains(event.target)) {
        setShowEntriesDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [issueRaiseDateFilter, statusFilter, priorityFilter, searchQuery, itemsPerPage]);

  const handleExport = () => {
    const headers = [
      'Log ID',
      'Issue Raise Date',
      'Completed Date',
      'Agency',
      'User Role',
      'User Name',
      'Issue Title',
      'Priority',
      'Status'
    ];

    const escapeCSV = (str) => {
      if (str === null || str === undefined) return '';
      const s = String(str);
      if (s.includes(',') || s.includes('"') || s.includes('\n')) {
        return '"' + s.replace(/"/g, '""') + '"';
      }
      return s;
    };

    const rows = [headers.map(escapeCSV)];
    filteredLogs.forEach(log => {
      rows.push([
        log.id,
        log.issueRaiseDate,
        log.completedDate,
        log.agency || '',
        log.userRole,
        log.userName,
        log.issueTitle,
        log.priority,
        log.status
      ].map(escapeCSV));
    });

    const csv = '\ufeff' + rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Support_Issue_Logs_Export_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleView = (log) => {
    const issueToView = issueLogs.find(l => l.id === log.id);
    setViewingIssue(issueToView);
    setShowViewModal(true);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setViewingIssue(null);
  };

  const handleMarkAsResolved = () => {
    if (viewingIssue) {
      setIssueLogs(prev => prev.map(log => 
        log.id === viewingIssue.id 
          ? { ...log, status: 'Resolved' }
          : log
      ));
      setViewingIssue(prev => prev ? { ...prev, status: 'Resolved' } : null);
    }
  };

  const handleEdit = (log) => {
    const issueToEdit = issueLogs.find(l => l.id === log.id);
    // Ensure existing attachments have preview URLs
    const attachmentsWithPreview = (issueToEdit.attachments || []).map(att => {
      if (att.preview) {
        return att;
      }
      // If it's an image but no preview, create a placeholder or use existing URL
      if (att.type && att.type.startsWith('image/')) {
        return {
          ...att,
          preview: att.url || `https://via.placeholder.com/300x200?text=${encodeURIComponent(att.name)}`
        };
      }
      // For PDFs
      if (att.type === 'application/pdf') {
        return {
          ...att,
          preview: att.url || att.preview || null
        };
      }
      return att;
    });
    
    const userInfo = getCurrentUserInfo();
    const agencyInfo = getCurrentAgencyInfo();
    setFormData({
      issueTitle: issueToEdit.issueTitle || '',
      issueDescription: issueToEdit.issueDescription || '',
      issueType: issueToEdit._raw?.issue_type || issueToEdit.issueType || 'Technical Issue',
      userName: issueToEdit.userName || userInfo.userName,
      mobileNumber: issueToEdit.mobileNumber || userInfo.mobileNumber,
      email: issueToEdit.email || userInfo.email,
      agency: issueToEdit.agency || agencyInfo.name, // Use current agency if not set
      agencyId: agencyInfo.id, // Store agency ID
      priority: issueToEdit.priority || 'Low',
      status: issueToEdit.status === 'Open' ? 'open' : issueToEdit.status === 'In Progress' ? 'in-progress' : 'resolved',
      attachments: attachmentsWithPreview
    });
    setEditingIssue(issueToEdit);
    setFormErrors({});
    setShowEditModal(true);
  };

  const handleAddNewIssue = () => {
    const userInfo = getCurrentUserInfo();
    const agencyInfo = getCurrentAgencyInfo();
    setFormData({
      issueTitle: '',
      issueDescription: '',
      issueType: 'Technical Issue',
      userName: userInfo.userName,
      mobileNumber: userInfo.mobileNumber,
      email: userInfo.email,
      agency: agencyInfo.name, // Auto-populate with current agency
      agencyId: agencyInfo.id, // Store agency ID
      priority: 'Low',
      status: 'open',
      attachments: []
    });
    setFormErrors({});
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setEditingIssue(null);
    const userInfo = getCurrentUserInfo();
    const agencyInfo = getCurrentAgencyInfo();
    setFormData({
      issueTitle: '',
      issueDescription: '',
      issueType: 'Technical Issue',
      userName: userInfo.userName,
      mobileNumber: userInfo.mobileNumber,
      email: userInfo.email,
      agency: agencyInfo.name, // Auto-populate with current agency
      agencyId: agencyInfo.id, // Store agency ID
      priority: 'Low',
      status: 'open',
      attachments: []
    });
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.issueTitle.trim()) {
      errors.issueTitle = 'Issue Title is required';
    }
    if (!formData.issueDescription.trim()) {
      errors.issueDescription = 'Issue Description is required';
    }
    if (!formData.issueType) {
      errors.issueType = 'Issue Type is required';
    }
    if (!formData.priority) {
      errors.priority = 'Priority is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitIssue = () => {
    if (!validateForm()) {
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    try {
      // Use the stored agency ID (agency users can only create issues for their own agency)
      const agencyId = formData.agencyId || getCurrentAgencyInfo().id;

      // Capitalize priority and status to match API enum values
      const capitalizePriority = (p) => {
        if (!p) return undefined;
        const lower = p.toLowerCase();
        if (lower === 'high') return 'High';
        if (lower === 'medium') return 'Medium';
        if (lower === 'low') return 'Low';
        if (lower === 'critical') return 'Critical';
        return p.charAt(0).toUpperCase() + p.slice(1);
      };

      const capitalizeStatus = (s) => {
        if (!s) return 'Open';
        const lower = s.toLowerCase();
        if (lower === 'open') return 'Open';
        if (lower === 'in-progress' || lower === 'in_progress') return 'In Progress';
        if (lower === 'resolved') return 'Resolved';
        if (lower === 'closed') return 'Closed';
        if (lower === 'cancelled') return 'Cancelled';
        return s;
      };

      const payload = {
        title: formData.issueTitle, // API expects 'title' not 'issue_title'
        description: formData.issueDescription, // API expects 'description' not 'issue_description'
        issue_type: formData.issueType || 'Technical Issue', // Required field
        priority: capitalizePriority(formData.priority),
        status: capitalizeStatus(formData.status),
      };

      // Only include agency_id if an agency was selected
      if (agencyId) {
        payload.agency_id = agencyId;
      }

      if (editingIssue && editingIssue._raw?.id) {
        await updateSupportIssue(editingIssue._raw.id, payload);
      } else if (editingIssue && editingIssue.id) {
        await updateSupportIssue(editingIssue.id, payload);
      } else {
        await createSupportIssue(payload);
      }

      setShowAddModal(false);
      setShowEditModal(false);
      setShowConfirmModal(false);
      handleCloseModal();
      fetchSupportIssues();
    } catch (error) {
      console.error('Error saving support issue:', error);
      alert(error.message || 'Failed to save support issue. Please try again.');
      setShowConfirmModal(false);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
  };

  const handleFileSelect = (file) => {
    if (file && file.size <= 10 * 1024 * 1024) { // 10MB limit
      const reader = new FileReader();
      reader.onload = (e) => {
        const fileObj = {
          id: Date.now(),
          file: file,
          name: file.name,
          size: file.size,
          type: file.type,
          preview: e.target.result
        };
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, fileObj]
        }));
      };
      if (file.type.startsWith('image/')) {
        reader.readAsDataURL(file);
      } else if (file.type === 'application/pdf') {
        reader.readAsDataURL(file);
      } else {
        const fileObj = {
          id: Date.now(),
          file: file,
          name: file.name,
          size: file.size,
          type: file.type,
          preview: null
        };
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, fileObj]
        }));
      }
    } else {
      alert('File size must be less than 10MB');
    }
  };

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      handleFileSelect(file);
    });
    // Reset input to allow selecting same file again
    e.target.value = '';
  };

  const handleRemoveAttachment = (id) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter(att => att.id !== id)
    }));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png'].includes(ext)) return '🖼️';
    if (ext === 'pdf') return '📄';
    return '📎';
  };

  // Get unique issue raise dates for filter
  const uniqueIssueRaiseDates = useMemo(() => {
    return [...new Set(issueLogs.map((log) => log.issueRaiseDate))]
      .filter(Boolean)
      .sort()
      .reverse();
  }, [issueLogs]);

  return (
    <div className="support-issue-logs-container">
      <div className="support-filters">
        <div className="filter-group">
          <div className="filter-wrapper" ref={issueRaiseDateDropdownRef}>
            <button
              className={`filter-btn ${issueRaiseDateFilter ? 'active' : ''}`}
              onClick={() => {
                setIssueRaiseDateDropdownOpen(!issueRaiseDateDropdownOpen);
                setStatusDropdownOpen(false);
                setPriorityDropdownOpen(false);
              }}
            >
              <span>📅</span> Issue Raise Date {issueRaiseDateFilter && `(${issueRaiseDateFilter})`}
              <span style={{ marginLeft: 'auto' }}>▼</span>
            </button>
            {issueRaiseDateDropdownOpen && (
              <div className="filter-dropdown">
                <div className="filter-option" onClick={() => { setIssueRaiseDateFilter(''); setIssueRaiseDateDropdownOpen(false); }}>
                  All Dates
                </div>
                {uniqueIssueRaiseDates.map(date => (
                  <div
                    key={date}
                    className={`filter-option ${issueRaiseDateFilter === date ? 'selected' : ''}`}
                    onClick={() => {
                      setIssueRaiseDateFilter(date);
                      setIssueRaiseDateDropdownOpen(false);
                    }}
                  >
                    {date}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="filter-wrapper" ref={statusDropdownRef}>
            <button
              className={`filter-btn ${statusFilter ? 'active' : ''}`}
              onClick={() => {
                setStatusDropdownOpen(!statusDropdownOpen);
                setIssueRaiseDateDropdownOpen(false);
                setPriorityDropdownOpen(false);
              }}
            >
              <span>📋</span> Status {statusFilter && `(${statusFilter})`}
              <span style={{ marginLeft: 'auto' }}>▼</span>
            </button>
            {statusDropdownOpen && (
              <div className="filter-dropdown">
                <div className="filter-option" onClick={() => { setStatusFilter(''); setStatusDropdownOpen(false); }}>
                  All Status
                </div>
                {statusOptions.map(status => (
                  <div
                    key={status}
                    className={`filter-option ${statusFilter === status ? 'selected' : ''}`}
                    onClick={() => {
                      setStatusFilter(status);
                      setStatusDropdownOpen(false);
                    }}
                  >
                    {status}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="filter-wrapper" ref={priorityDropdownRef}>
            <button
              className={`filter-btn ${priorityFilter ? 'active' : ''}`}
              onClick={() => {
                setPriorityDropdownOpen(!priorityDropdownOpen);
                setIssueRaiseDateDropdownOpen(false);
                setStatusDropdownOpen(false);
              }}
            >
              <span>⚡</span> Priority {priorityFilter && `(${priorityFilter})`}
              <span style={{ marginLeft: 'auto' }}>▼</span>
            </button>
            {priorityDropdownOpen && (
              <div className="filter-dropdown">
                <div className="filter-option" onClick={() => { setPriorityFilter(''); setPriorityDropdownOpen(false); }}>
                  All Priority
                </div>
                {priorityOptions.map(priority => (
                  <div
                    key={priority}
                    className={`filter-option ${priorityFilter === priority ? 'selected' : ''}`}
                    onClick={() => {
                      setPriorityFilter(priority);
                      setPriorityDropdownOpen(false);
                    }}
                  >
                    {priority}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="support-actions">
          <button className="btn-primary" onClick={handleAddNewIssue}>
            <span>+</span> Add New Issue
          </button>
          <button className="btn-secondary" onClick={handleExport} title="Export">
            <span>📊</span>
          </button>
        </div>
      </div>

      <div className="table-controls">
        <div className="show-entries" ref={showEntriesDropdownRef}>
          <span>Show</span>
          <button
            className="entries-btn"
            onClick={() => setShowEntriesDropdownOpen(!showEntriesDropdownOpen)}
          >
            {itemsPerPage} entries
            <span style={{ marginLeft: '8px' }}>▼</span>
          </button>
          {showEntriesDropdownOpen && (
            <div className="entries-dropdown">
              {[10, 25, 50, 100].map(num => (
                <div
                  key={num}
                  className="entries-option"
                  onClick={() => {
                    setItemsPerPage(num);
                    setShowEntriesDropdownOpen(false);
                    setCurrentPage(1);
                  }}
                >
                  {num} entries
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="">
          <input
            type="text"
            className="search-input"
            placeholder="Search by Log ID or User Name"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <span className="search-icon">🔍</span>
        </div>
      </div>

      <div className="support-table-container">
        {error && (
          <div className="error-message" style={{ marginBottom: '8px' }}>
            {error}
          </div>
        )}
        {loading && (
          <div style={{ padding: '16px', color: '#6B7280' }}>Loading issues...</div>
        )}
        <table className="support-table">
          <thead>
            <tr>
              <th>
                Log ID
                <span className="sort-icon">⇅</span>
              </th>
              <th>Issue Raise Date</th>
              <th>Completed Date</th>
              <th>Agency</th>
              <th>User Role</th>
              <th>User Name</th>
              <th>Issue Title</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentLogs.length === 0 ? (
              <tr>
                <td colSpan="10" style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                  No issue logs found
                </td>
              </tr>
            ) : (
              currentLogs.map((log) => (
                <tr key={log.id}>
                  <td>{log.id}</td>
                  <td>{log.issueRaiseDate}</td>
                  <td>{log.completedDate}</td>
                  <td>{log.agency || '-'}</td>
                  <td>{log.userRole}</td>
                  <td>{log.userName}</td>
                  <td>{log.issueTitle}</td>
                  <td>
                    <span className={`priority-badge ${log.priority.toLowerCase().replace(' ', '-')}`}>
                      {log.priority}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${log.status.toLowerCase().replace(' ', '-')}`}>
                      {log.status}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="action-btn view-btn" title="View" onClick={() => handleView(log)}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M8 3C4.5 3 1.73 5.11 0 8C1.73 10.89 4.5 13 8 13C11.5 13 14.27 10.89 16 8C14.27 5.11 11.5 3 8 3ZM8 11C6.34 11 5 9.66 5 8C5 6.34 6.34 5 8 5C9.66 5 11 6.34 11 8C11 9.66 9.66 11 8 11ZM8 6.5C7.17 6.5 6.5 7.17 6.5 8C6.5 8.83 7.17 9.5 8 9.5C8.83 9.5 9.5 8.83 9.5 8C9.5 7.17 8.83 6.5 8 6.5Z" fill="#2563EB"/>
                        </svg>
                      </button>
                      <button className="action-btn edit-btn" title="Edit" onClick={() => handleEdit(log)}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M11.333 2C11.5084 1.82464 11.7163 1.68603 11.9441 1.59229C12.1719 1.49856 12.4151 1.45172 12.6607 1.45172C12.9062 1.45172 13.1494 1.49856 13.3772 1.59229C13.605 1.68603 13.8129 1.82464 13.9883 2C14.1637 2.17536 14.3023 2.38328 14.396 2.61108C14.4898 2.83888 14.5366 3.08206 14.5366 3.32767C14.5366 3.57327 14.4898 3.81645 14.396 4.04425C14.3023 4.27205 14.1637 4.47997 13.9883 4.65533L5.32467 13.319L2 14L2.68067 10.6753L11.333 2Z" stroke="#6366F1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="support-pagination">
        <div className="pagination-info">
          Showing {filteredLogs.length === 0 ? 0 : startIndex + 1} to{' '}
          {Math.min(endIndex, totalCount)} of {totalCount} entries
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            ‹
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </button>
          ))}
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            ›
          </button>
        </div>
      </div>

      {/* Add New Issue Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="add-issue-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Add New Issue</h2>
              <button className="action-btn" onClick={handleCloseModal} style={{ fontSize: '1.2rem', padding: '4px 8px' }}>✖</button>
            </div>
            <div className="modal-subtitle">Fill in the details to submit a new support issue</div>

            <div className="modal-body">
              {/* Issue Details */}
              <div className="form-section">
                <h3 className="section-title">Issue Details</h3>
                <div className="form-grid">
                  <div className="form-field full-width">
                    <label>
                      Issue Title <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${formErrors.issueTitle ? 'error' : ''}`}
                      placeholder="E.g., Unable to download invoice"
                      value={formData.issueTitle}
                      onChange={(e) => setFormData(prev => ({ ...prev, issueTitle: e.target.value }))}
                    />
                    {formErrors.issueTitle && (
                      <div className="error-message">{formErrors.issueTitle}</div>
                    )}
                  </div>

                  <div className="form-field full-width">
                    <label>
                      Issue Description <span className="required-asterisk">*</span>
                    </label>
                    <textarea
                      className={`form-input ${formErrors.issueDescription ? 'error' : ''}`}
                      placeholder="Please provide detailed explanation of the problem"
                      rows="4"
                      value={formData.issueDescription}
                      onChange={(e) => setFormData(prev => ({ ...prev, issueDescription: e.target.value }))}
                    />
                    {formErrors.issueDescription && (
                      <div className="error-message">{formErrors.issueDescription}</div>
                    )}
                  </div>

                  <div className="form-field">
                    <label>
                      Issue Type <span className="required-asterisk">*</span>
                    </label>
                    <select
                      className={`form-input ${formErrors.issueType ? 'error' : ''}`}
                      value={formData.issueType}
                      onChange={(e) => setFormData(prev => ({ ...prev, issueType: e.target.value }))}
                    >
                      {issueTypeOptions.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                    {formErrors.issueType && (
                      <div className="error-message">{formErrors.issueType}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* User Information */}
              <div className="form-section">
                <h3 className="section-title">User Information</h3>
                <div className="form-grid">
                  <div className="form-field">
                    <label>User Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.userName}
                      readOnly
                    />
                  </div>

                  <div className="form-field">
                    <label>Mobile Number</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.mobileNumber}
                      readOnly
                    />
                  </div>

                  <div className="form-field">
                    <label>Email</label>
                    <input
                      type="email"
                      className="form-input"
                      value={formData.email}
                      readOnly
                    />
                  </div>
                </div>
              </div>

              {/* Categorization */}
              <div className="form-section">
                <h3 className="section-title">Categorization</h3>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Agency</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.agency || getCurrentAgencyInfo().name}
                      readOnly
                      style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                    />
                  </div>

                  <div className="form-field">
                    <label>
                      Priority <span className="required-asterisk">*</span>
                    </label>
                    <div className="priority-radio-group">
                      {priorityOptions.map(priority => (
                        <label key={priority} className={`priority-radio ${formData.priority === priority ? 'selected' : ''}`}>
                          <input
                            type="radio"
                            name="priority"
                            value={priority}
                            checked={formData.priority === priority}
                            onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                          />
                          <span>{priority}</span>
                        </label>
                      ))}
                    </div>
                    {formErrors.priority && (
                      <div className="error-message">{formErrors.priority}</div>
                    )}
                  </div>

                  <div className="form-field">
                    <label>Status</label>
                    <select
                      className="form-input"
                      value={formData.status}
                      onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                    >
                      <option value="open">Open</option>
                      <option value="in-progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Attachment / Screenshot */}
              <div className="form-section">
                <h3 className="section-title">Attachment / Screenshot</h3>
                <div
                  className={`file-upload-area ${isDragging ? 'dragging' : ''}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('issue-file-input').click()}
                >
                  <div className="upload-icon">☁️</div>
                  <div className="upload-text">Drag files here or click to browse</div>
                  <div className="upload-hint">Supports JPG, PNG, PDF up to 10MB</div>
                  <button className="browse-files-btn" onClick={(e) => { e.stopPropagation(); document.getElementById('issue-file-input').click(); }}>
                    Browse Files
                  </button>
                  <input
                    id="issue-file-input"
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    multiple
                    onChange={handleFileInputChange}
                    style={{ display: 'none' }}
                  />
                </div>

                {formData.attachments.length > 0 && (
                  <div className="attachments-grid">
                    {formData.attachments.map(attachment => (
                      <div key={attachment.id} className="attachment-preview-item">
                        {attachment.preview && attachment.type.startsWith('image/') ? (
                          <div className="attachment-image-wrapper">
                            <img src={attachment.preview} alt={attachment.name} className="attachment-preview-image" />
                            <button
                              className="attachment-remove-overlay-btn"
                              onClick={() => handleRemoveAttachment(attachment.id)}
                              title="Remove"
                            >
                              ✖
                            </button>
                          </div>
                        ) : attachment.preview && attachment.type === 'application/pdf' ? (
                          <div className="attachment-pdf-wrapper">
                            <iframe src={attachment.preview} className="attachment-preview-pdf" title={attachment.name} />
                            <button
                              className="attachment-remove-overlay-btn"
                              onClick={() => handleRemoveAttachment(attachment.id)}
                              title="Remove"
                            >
                              ✖
                            </button>
                          </div>
                        ) : (
                          <div className="attachment-file-wrapper">
                            <span className="attachment-icon">{getFileIcon(attachment.name)}</span>
                            <span className="attachment-name">{attachment.name}</span>
                            <span className="attachment-size">({formatFileSize(attachment.size)})</span>
                            <button
                              className="attachment-remove-btn"
                              onClick={() => handleRemoveAttachment(attachment.id)}
                            >
                              ✖
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-primary" onClick={handleSubmitIssue}>
                Submit Issue
              </button>
              <a href="#" className="cancel-link" onClick={(e) => { e.preventDefault(); handleCloseModal(); }}>
                Cancel
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Edit Issue Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="add-issue-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Edit Issue</h2>
              <button className="action-btn" onClick={handleCloseModal} style={{ fontSize: '1.2rem', padding: '4px 8px' }}>✖</button>
            </div>
            <div className="modal-subtitle">Fill in the details to submit a new support issue</div>

            <div className="modal-body">
              {/* Issue Details */}
              <div className="form-section">
                <h3 className="section-title">Issue Details</h3>
                <div className="form-grid">
                  <div className="form-field full-width">
                    <label>
                      Issue Title <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${formErrors.issueTitle ? 'error' : ''}`}
                      placeholder="E.g., Unable to download invoice"
                      value={formData.issueTitle}
                      onChange={(e) => setFormData(prev => ({ ...prev, issueTitle: e.target.value }))}
                    />
                    {formErrors.issueTitle && (
                      <div className="error-message">{formErrors.issueTitle}</div>
                    )}
                  </div>

                  <div className="form-field full-width">
                    <label>
                      Issue Description <span className="required-asterisk">*</span>
                    </label>
                    <textarea
                      className={`form-input ${formErrors.issueDescription ? 'error' : ''}`}
                      placeholder="Please provide detailed explanation of the problem"
                      rows="4"
                      value={formData.issueDescription}
                      onChange={(e) => setFormData(prev => ({ ...prev, issueDescription: e.target.value }))}
                    />
                    {formErrors.issueDescription && (
                      <div className="error-message">{formErrors.issueDescription}</div>
                    )}
                  </div>

                  <div className="form-field">
                    <label>
                      Issue Type <span className="required-asterisk">*</span>
                    </label>
                    <select
                      className={`form-input ${formErrors.issueType ? 'error' : ''}`}
                      value={formData.issueType}
                      onChange={(e) => setFormData(prev => ({ ...prev, issueType: e.target.value }))}
                    >
                      {issueTypeOptions.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                    {formErrors.issueType && (
                      <div className="error-message">{formErrors.issueType}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* User Information */}
              <div className="form-section">
                <h3 className="section-title">User Information</h3>
                <div className="form-grid">
                  <div className="form-field">
                    <label>User Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.userName}
                      readOnly
                    />
                  </div>

                  <div className="form-field">
                    <label>Mobile Number</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.mobileNumber}
                      readOnly
                    />
                  </div>

                  <div className="form-field">
                    <label>Email</label>
                    <input
                      type="email"
                      className="form-input"
                      value={formData.email}
                      readOnly
                    />
                  </div>
                </div>
              </div>

              {/* Categorization */}
              <div className="form-section">
                <h3 className="section-title">Categorization</h3>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Agency</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.agency || getCurrentAgencyInfo().name}
                      readOnly
                      style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                    />
                  </div>

                  <div className="form-field">
                    <label>
                      Priority <span className="required-asterisk">*</span>
                    </label>
                    <div className="priority-radio-group">
                      {priorityOptions.map(priority => (
                        <label key={priority} className={`priority-radio ${formData.priority === priority ? 'selected' : ''}`}>
                          <input
                            type="radio"
                            name="priority"
                            value={priority}
                            checked={formData.priority === priority}
                            onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                          />
                          <span>{priority}</span>
                        </label>
                      ))}
                    </div>
                    {formErrors.priority && (
                      <div className="error-message">{formErrors.priority}</div>
                    )}
                  </div>

                  <div className="form-field">
                    <label>Status</label>
                    <select
                      className="form-input"
                      value={formData.status}
                      onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                    >
                      <option value="open">Open</option>
                      <option value="in-progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Attachment / Screenshot */}
              <div className="form-section">
                <h3 className="section-title">Attachment / Screenshot</h3>
                <div
                  className={`file-upload-area ${isDragging ? 'dragging' : ''}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('edit-issue-file-input').click()}
                >
                  <div className="upload-icon">☁️</div>
                  <div className="upload-text">Drag files here or click to browse</div>
                  <div className="upload-hint">Supports JPG, PNG, PDF up to 10MB</div>
                  <button className="browse-files-btn" onClick={(e) => { e.stopPropagation(); document.getElementById('edit-issue-file-input').click(); }}>
                    Browse Files
                  </button>
                  <input
                    id="edit-issue-file-input"
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    multiple
                    onChange={handleFileInputChange}
                    style={{ display: 'none' }}
                  />
                </div>

                {formData.attachments.length > 0 && (
                  <div className="attachments-grid">
                    {formData.attachments.map(attachment => (
                      <div key={attachment.id} className="attachment-preview-item">
                        {attachment.preview && attachment.type.startsWith('image/') ? (
                          <div className="attachment-image-wrapper">
                            <img src={attachment.preview} alt={attachment.name} className="attachment-preview-image" />
                            <button
                              className="attachment-remove-overlay-btn"
                              onClick={() => handleRemoveAttachment(attachment.id)}
                              title="Remove"
                            >
                              ✖
                            </button>
                          </div>
                        ) : attachment.preview && attachment.type === 'application/pdf' ? (
                          <div className="attachment-pdf-wrapper">
                            <iframe src={attachment.preview} className="attachment-preview-pdf" title={attachment.name} />
                            <button
                              className="attachment-remove-overlay-btn"
                              onClick={() => handleRemoveAttachment(attachment.id)}
                              title="Remove"
                            >
                              ✖
                            </button>
                          </div>
                        ) : (
                          <div className="attachment-file-wrapper">
                            <span className="attachment-icon">{getFileIcon(attachment.name)}</span>
                            <span className="attachment-name">{attachment.name}</span>
                            <span className="attachment-size">({formatFileSize(attachment.size)})</span>
                            <button
                              className="attachment-remove-btn"
                              onClick={() => handleRemoveAttachment(attachment.id)}
                            >
                              ✖
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-primary" onClick={handleSubmitIssue}>
                Save
              </button>
              <a href="#" className="cancel-link" onClick={(e) => { e.preventDefault(); handleCloseModal(); }}>
                Cancel
              </a>
            </div>
          </div>
        </div>
      )}

      {/* View Issue Modal */}
      {showViewModal && viewingIssue && (
        <div className="modal-overlay" onClick={handleCloseViewModal}>
          <div className="view-issue-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="view-issue-header">
              <h2 className="view-issue-title">Issue Details - {viewingIssue.id}</h2>
              <button className="view-issue-close-btn" onClick={handleCloseViewModal}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            <div className="view-issue-tabs">
              <button className="view-issue-tab active">Details</button>
            </div>

            <div className="view-issue-body">
              <div className="view-issue-grid">
                <div className="view-issue-left-column">
                  <div className="view-issue-field">
                    <span className="view-issue-field-label">Log ID:</span>
                    <span className="view-issue-field-value">{viewingIssue.id}</span>
                  </div>
                  <div className="view-issue-field">
                    <span className="view-issue-field-label">Reported By:</span>
                    <span className="view-issue-field-value">{viewingIssue.userName}</span>
                  </div>
                  <div className="view-issue-field">
                    <span className="view-issue-field-label">Agency:</span>
                    <span className="view-issue-field-value">{viewingIssue.agency || '-'}</span>
                  </div>
                  <div className="view-issue-field">
                    <span className="view-issue-field-label">Email ID:</span>
                    <span className="view-issue-field-value">{viewingIssue.email || '-'}</span>
                  </div>
                  <div className="view-issue-field">
                    <span className="view-issue-field-label">Issue Title:</span>
                    <span className="view-issue-field-value">{viewingIssue.issueTitle}</span>
                  </div>
                  <div className="view-issue-field full-width">
                    <span className="view-issue-field-label">Description:</span>
                    <div className="view-issue-description-text">{viewingIssue.issueDescription || '-'}</div>
                  </div>
                </div>

                <div className="view-issue-right-column">
                  <div className="view-issue-field">
                    <span className="view-issue-field-label">Status:</span>
                    <span className={`view-issue-status-badge ${viewingIssue.status.toLowerCase().replace(' ', '-')}`}>
                      {viewingIssue.status}
                    </span>
                  </div>
                  <div className="view-issue-field">
                    <span className="view-issue-field-label">Date Submitted:</span>
                    <span className="view-issue-field-value">{viewingIssue.issueRaiseDate}</span>
                  </div>
                  <div className="view-issue-field">
                    <span className="view-issue-field-label">Customer Name:</span>
                    <span className="view-issue-field-value">{viewingIssue.customerName || viewingIssue.userName}</span>
                  </div>
                  <div className="view-issue-field">
                    <span className="view-issue-field-label">Mobile Number:</span>
                    <span className="view-issue-field-value">{viewingIssue.mobileNumber || '-'}</span>
                  </div>
                </div>
              </div>

              {viewingIssue.attachments && viewingIssue.attachments.length > 0 && (
                <div className="view-issue-screenshots-section">
                  <h3 className="view-issue-screenshots-title">Uploaded Screenshots</h3>
                  <div className="view-issue-screenshots-grid">
                    {viewingIssue.attachments.map((attachment, index) => (
                      <div key={attachment.id || index} className="view-issue-screenshot-item">
                        {attachment.preview && attachment.type && attachment.type.startsWith('image/') ? (
                          <img 
                            src={attachment.preview} 
                            alt={attachment.name || 'Screenshot'} 
                            className="view-issue-screenshot-image"
                          />
                        ) : attachment.preview && attachment.type === 'application/pdf' ? (
                          <iframe 
                            src={attachment.preview} 
                            className="view-issue-screenshot-pdf"
                            title={attachment.name || 'Document'}
                          />
                        ) : (
                          <div className="view-issue-screenshot-placeholder">
                            <span className="view-issue-screenshot-icon">{getFileIcon(attachment.name || 'file')}</span>
                            <span className="view-issue-screenshot-name">{attachment.name || 'File'}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="view-issue-footer">
              <button className="view-issue-edit-btn" onClick={() => {
                handleCloseViewModal();
                handleEdit(viewingIssue);
              }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11.333 2C11.5084 1.82464 11.7163 1.68603 11.9441 1.59229C12.1719 1.49856 12.4151 1.45172 12.6607 1.45172C12.9062 1.45172 13.1494 1.49856 13.3772 1.59229C13.605 1.68603 13.8129 1.82464 13.9883 2C14.1637 2.17536 14.3023 2.38328 14.396 2.61108C14.4898 2.83888 14.5366 3.08206 14.5366 3.32767C14.5366 3.57327 14.4898 3.81645 14.396 4.04425C14.3023 4.27205 14.1637 4.47997 13.9883 4.65533L5.32467 13.319L2 14L2.68067 10.6753L11.333 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                </svg>
                Edit
              </button>
              {viewingIssue.status !== 'Resolved' && (
                <button className="view-issue-resolve-btn" onClick={handleMarkAsResolved}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13 4L6 11L3 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Mark as Resolved
                </button>
              )}
              <button className="view-issue-close-footer-btn" onClick={handleCloseViewModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="modal-overlay" onClick={handleCancelConfirm}>
          <div className="confirm-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-modal-header">
              <div className="confirm-icon-wrapper">
                <div className="confirm-warning-icon">⚠️</div>
              </div>
              <h2 className="confirm-modal-title">Confirm Action</h2>
              <button className="confirm-close-btn" onClick={handleCancelConfirm}>✖</button>
            </div>
            <div className="confirm-modal-body">
              <p className="confirm-message">Are you sure you want to submit this issue?</p>
            </div>
            <div className="confirm-modal-footer">
              <button className="confirm-cancel-btn" onClick={handleCancelConfirm}>
                Cancel
              </button>
              <button className="confirm-submit-btn" onClick={handleConfirmSubmit}>
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AgencySupportIssues;
